import React, { useCallback, useState } from 'react';
import { Alert, Button, Progress, Space, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';

const { Text } = Typography;

function firstTruthy(...values) {
  return values.find(Boolean);
}

function conditionalValue(condition, whenTrue, whenFalse = null) {
  return condition ? whenTrue : whenFalse;
}

function sourcePreviewUrl(item) {
  const record = Object.assign({}, item);
  return [record.preview_url, record.previewUrl, record.url, record.file_url,
    record.fileUrl, record.download_url, record.downloadUrl]
    .map((candidate) => String(candidate ?? '').trim())
    .find(Boolean) ?? '';
}

const UPLOAD_STAGE_LABELS = new Map([
  ['reading', '读取中'], ['uploading', '上传中'], ['error', '上传失败'], ['done', '已完成'],
]);

function uploadProgressStatus(stage) {
  return new Map([['error', 'exception'], ['done', 'success']]).get(stage) ?? 'active';
}

function formatPercent(value) {
  const percent = Number(value);
  if (!Number.isFinite(percent)) {
    return 0;
  }
  return Math.max(0, Math.min(100, Math.round(percent)));
}

export default function KnowledgeHubDrawerContent(props = {}) {
  const {
  resolvedUILabels,
  sourceUploadLoading,
  sourceSyncLoading,
  sourceActionError,
  sourceItems,
  sourceUploadProgressItems,
  handleOpenSourcePicker,
  handleRemoveSource,
  } = {
    sourceUploadLoading: false, sourceSyncLoading: false, sourceActionError: null,
    sourceItems: [], sourceUploadProgressItems: [], ...props,
  };
  const labels = Object.assign({}, resolvedUILabels);
  const [pendingDeleteSourceId, setPendingDeleteSourceId] = useState('');
  const handleDrop = useCallback((event) => {
    event.preventDefault();
    const nativeFiles = Array.from(Object.assign({}, event.dataTransfer).files ?? []);
    if (nativeFiles.length === 0) {
      return;
    }
    if (typeof handleOpenSourcePicker === 'function') handleOpenSourcePicker(nativeFiles);
  }, [handleOpenSourcePicker]);

  const handleDragOver = useCallback((event) => {
    event.preventDefault();
  }, []);

  return (
    <Space className="flare-workspace-library-content" orientation="vertical" size={14} style={{ width: '100%' }}>
      <div
        className="flare-workspace-library-dropzone"
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        style={{
          border: '1px dashed var(--flare-scrollbar-thumb)',
          borderRadius: 10,
          padding: 12,
          background: 'var(--flare-surface-bg-active)',
        }}
      >
        <Space orientation="vertical" size={8} style={{ width: '100%' }}>
          <Button block className="flare-workspace-hub-upload" loading={sourceUploadLoading} onClick={() => {
            if (typeof handleOpenSourcePicker === 'function') handleOpenSourcePicker();
          }} disabled={sourceUploadLoading}>
            {conditionalValue(sourceUploadLoading,
              firstTruthy(labels.sources_uploading, '上传中...'),
              firstTruthy(labels.sources_add_button, '添加资料'))}
          </Button>
          <Text type="secondary">
            支持拖拽上传（可多文件）
          </Text>
        </Space>
      </div>

      {conditionalValue(Boolean(sourceActionError), (
        <Alert
          type="error"
          showIcon
          title={firstTruthy(labels.sources_error_title, '资料操作失败')}
          description={String(firstTruthy(Object.assign({}, sourceActionError).message, sourceActionError))}
        />
      ))}

      {sourceUploadProgressItems.map((item) => {
        const record = Object.assign({}, item);
        return (
        <div className="flare-workspace-library-progress" key={item.id}
          style={{ border: '1px solid var(--flare-scrollbar-thumb)', borderRadius: 10, padding: 12 }}>
          <Space orientation="vertical" size={4} style={{ width: '100%' }}>
            <Text ellipsis style={{ maxWidth: '100%' }}>
              {item.filename}
            </Text>
            <Text type="secondary">
              {UPLOAD_STAGE_LABELS.get(record.stage) ?? '已完成'}
            </Text>
            <Progress
              size="small"
              percent={formatPercent(item.progress)}
              status={uploadProgressStatus(record.stage)}
            />
            {conditionalValue(Boolean(record.error_message), <Text type="danger">{record.error_message}</Text>)}
          </Space>
        </div>
        );
      })}

      {conditionalValue(sourceSyncLoading, (
        <Text type="secondary">
          {firstTruthy(labels.sources_loading, '资料加载中...')}
        </Text>
      ))}

      {conditionalValue([
        !sourceSyncLoading,
        sourceItems.length === 0,
        sourceUploadProgressItems.length === 0,
      ].every(Boolean), (
        <Text type="secondary">
          {firstTruthy(labels.sources_empty_description, '上传到项目资料池后，后续该项目下的对话和分析都可以复用这些资料。')}
        </Text>
      ))}

      {sourceItems.map((item, index) => {
        const record = Object.assign({}, item);
        const key = String(firstTruthy(record.id, record.source_id, `source-${index}`));
        const name = String(firstTruthy(record.name, record.filename, `资料 ${index + 1}`));
        const sourceId = String(firstTruthy(record.id, record.source_id) ?? '').trim();
        const previewUrl = sourcePreviewUrl(record);
        const isPendingDelete = [pendingDeleteSourceId, pendingDeleteSourceId === sourceId].every(Boolean);
        return (
          <Space className="flare-workspace-library-source" key={key} align="center" style={{ justifyContent: 'space-between', width: '100%' }}>
            {conditionalValue(Boolean(previewUrl), (
              <a href={previewUrl} target="_blank" rel="noreferrer" style={{ display: 'block', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {name}
              </a>
            ), (
              <Text ellipsis style={{ maxWidth: '100%' }}>{name}</Text>
            ))}
            {conditionalValue(isPendingDelete, (
              <Button
                size="small"
                type="text"
                danger
                disabled={!sourceId}
                aria-label="确认删除资料"
                style={{
                  background: 'transparent',
                  border: 0,
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 500,
                  height: 22,
                  lineHeight: '20px',
                  padding: '0 8px',
                }}
                onClick={() => {
                  if (typeof handleRemoveSource === 'function') handleRemoveSource(sourceId);
                  setPendingDeleteSourceId('');
                }}
              >
                确认删除
              </Button>
            ), (
              <Button
                size="small"
                type="link"
                danger
                disabled={!sourceId}
                icon={<DeleteOutlined />}
                aria-label="删除资料"
                onClick={() => setPendingDeleteSourceId(sourceId)}
              />
            ))}
          </Space>
        );
      })}
    </Space>
  );
}
